import { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useStore } from 'store/StoreContext';
import { FormErrors } from 'validators/ForgotPasswordValidator';
import { callUpdateUserBulkApi, callSearchUsersApi } from 'requests/RequestUser';
import { Passwords } from 'components/templates/ForgotPasswordUpdateTemplate';
import { RoutePath } from 'consts/RoutePath';
import { HttpStatus } from 'consts/HttpStatus';
import { ReactError } from 'consts/ReactError';
import { CONFIRM_ERROR_PASSWORD, HARD_TO_GUESS_PASSWORD } from 'utils/messages';

/**
 * パスワード忘れ 更新処理のカスタムフック
 * フォームバリデーション -> 更新実処理 -> ページ遷移 を段階的に分けている
 * useStateが非同期なのでuseEffectを使って段階的にしないとエラーをキャッチすることができないため
 */
export const useUpdateForgotPassword = (
	formErrors: FormErrors,
	setFormErrors: React.Dispatch<React.SetStateAction<FormErrors>>,
	getErrorMessage: Function,
	actionError: string,
	setActionError: React.Dispatch<React.SetStateAction<string>>,
	formData: Passwords,
	handleDialogOpen: Function,
	setProcessLoading: React.Dispatch<React.SetStateAction<boolean>>,
	confirmGid: string,
) => {
	const [store] = useStore();
	const history = useHistory();

	// フォームバリデーション
	const [isFormValidationFinished, setIsFormValidationFinished] = useState(false);
	const update = async () => {
		setProcessLoading(true);

		Object.keys(formErrors).forEach((k) => {
			setFormErrors((prev) => ({ ...prev, [k]: getErrorMessage(k, formData[k]) }));
		});
		const isPasswordsMatched = formData.newPassword === formData.newPasswordConfirm;
		const hardToGuessPassword = confirmGid.toLowerCase() !== formData.newPassword.toLowerCase();
		if (!isPasswordsMatched) {
			setActionError(CONFIRM_ERROR_PASSWORD);
			setIsUpdateFinished(true);
			return;
		}
		// GIDと同じパスワードを設定できない
		if (!hardToGuessPassword) {
			setActionError(HARD_TO_GUESS_PASSWORD);
			setIsUpdateFinished(true);
			return;
		}
		setActionError('');
		setIsFormValidationFinished(true);
	};

	// 更新実処理
	const [isUpdateFinished, setIsUpdateFinished] = useState(false);
	useEffect(() => {
		if (!isFormValidationFinished) {
			return;
		}

		const isError = !!Object.values(formErrors).find((e) => e !== '');
		if (isError) {
			setIsFormValidationFinished(false);
			setProcessLoading(false);
			return;
		}

		const executeUpdate = async () => {
			// メールアドレスから個人情報を検索する
			if (!store.forgotPasswordData.mailaddress) {
				setActionError('メールアドレスが存在しません。');
				setIsFormValidationFinished(false);
				setProcessLoading(false);
				return;
			}
			const searchUsersResult = await callSearchUsersApi({
				mailaddress_1: store.forgotPasswordData.mailaddress,
				withdrawal_flg: 0,
			});
			if (searchUsersResult.status !== HttpStatus.OK || !searchUsersResult.data) {
				setActionError(ReactError.E9999);
				history.push(RoutePath.ERROR);
				return;
			}
			const users = searchUsersResult.data.user_list;
			if (users.length === 0) {
				setActionError('メールアドレスに対応するGakkenIDが存在しません。');
				setIsFormValidationFinished(false);
				setProcessLoading(false);
				return;
			}
			const user =
				users.length === 1 ? users[0] : users.find((u) => u.mailaddress_1 === store.forgotPasswordData.mailaddress);
			if (!user.gid) {
				setActionError('GIDが登録されていません。GakkenID事務局へお問い合わせください。');
				setIsFormValidationFinished(false);
				setProcessLoading(false);
				return;
			}

			// 個人情報更新（バルク）でパスワードを更新する
			const updateUsersBulkResult = await callUpdateUserBulkApi({
				user_list: [{ gidpk: user.gidpk, pass: formData.newPassword }],
			});
			if (updateUsersBulkResult.status !== HttpStatus.OK) {
				setActionError(ReactError.E9999);
				history.push(RoutePath.ERROR);
				return;
			}
			setIsUpdateFinished(true);
		};
		executeUpdate();
	}, [isFormValidationFinished]);

	// 完了ダイアログ表示
	useEffect(() => {
		if (!isUpdateFinished) {
			return;
		}

		const isError = actionError !== '';
		if (isError) {
			setIsFormValidationFinished(false);
			setIsUpdateFinished(false);
			setProcessLoading(false);
			return;
		}

		handleDialogOpen();
	}, [isUpdateFinished]);

	return {
		update,
	};
};
